import {computed, Injectable, signal} from '@angular/core';
import {BannerSocialFileData} from "../types/banners-social-modal.types";

@Injectable({
  providedIn: 'root'
})
export class BannerSocialFilesStateService {
  private _bannerSocialFilesState = signal<Array<BannerSocialFileData>>([]);
  public bannerSocialFilesState = computed(() =>
    [...this._bannerSocialFilesState()].sort((a, b) =>
      (a.priority ?? Infinity) - (b.priority ?? Infinity)
    ));
  public readonly bannerSocialFileIds = computed(() =>
    this._bannerSocialFilesState()
      .map(file => file.file.id)
      .sort((a, b) => a - b)
  );
  public rawBannerSocialFiles = computed(() => this._bannerSocialFilesState());

  public setBannerSocialFilesState(files: Array<BannerSocialFileData>): void {
    this._bannerSocialFilesState.set(files);
  }

  public updateBannerSocialFilesState(file: BannerSocialFileData): void {
    this._bannerSocialFilesState.update(files => {
      const existingFileIndex = files.findIndex(f => f.id === file.id);

      return existingFileIndex !== -1 ?
        files.map(f => f.id === file.id ? file : f) :
        [...files, file];
    });
  }

  public deleteFileById(fileId: number): void {
    this._bannerSocialFilesState.update(files =>
      files.filter(file => file.id !== fileId)
    );
  }

  public hasFileWithPriority(priority: number): boolean {
    return this.bannerSocialFilesState().some(file => file.priority === priority);
  }

  public getNextFreePriority(): number {
    const priorities = this.rawBannerSocialFiles()
      .map(f => f.priority)
      .filter((p): p is number => p != null)
      .sort((a, b) => a - b);

    if (priorities.length === 0) { return 1; }

    if (priorities[0] !== 1) { return 1; }

    for (let i = 0; i < priorities.length - 1; i++) {
      if (priorities[i + 1] !== priorities[i] + 1) {
        return priorities[i] + 1;
      }
    }

    return priorities[priorities.length - 1] + 1;
  }

  public getRemainingFileSlots(): number {
    return 99 - this.bannerSocialFilesState().length;
  }

  public resetBannerSocialFilesState(): void {
    this._bannerSocialFilesState.set([]);
  }
}
